<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script setup lang="ts">
import type { HeaderLogo, HeaderSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed, ref } from 'vue';

const props = defineProps<{
  section: HeaderSection;
}>();

const reportStore = useUpdatedReportStore();

const isEditing = computed(() => reportStore.isEditing);

const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const defaultLogo = computed(() => props.section.logo);

const addedLogo = computed<HeaderLogo | null>(() => {
  const dynamic = reportStore.getValue('header.addedLogo');
  if (dynamic && typeof dynamic === 'object' && (dynamic as any).url) {
    return dynamic as HeaderLogo;
  }
  return props.section.addedLogo ?? null;
});

const logoHeight = computed(() => props.section.logoHeight ?? 50);

// Style for default logo image
const defaultLogoStyle = computed(() => {
  const h = logoHeight.value;
  const logo = defaultLogo.value;
  if (logo && logo.width && logo.height) {
    const ratio = logo.width / logo.height;
    return {
      height: `${h}px`,
      width: `${h * ratio}px`,
    };
  }
  return {
    height: `${h}px`,
  };
});

// Style for added logo image
const addedLogoStyle = computed(() => {
  const h = logoHeight.value;
  const logo = addedLogo.value;
  if (logo && logo.width && logo.height) {
    const ratio = logo.width / logo.height;
    return {
      height: `${h}px`,
      width: `${h * ratio}px`,
    };
  }
  return {
    height: `${h}px`,
  };
});

const resolvedAddedLogoUrl = computed(() => {
  const logo = addedLogo.value;
  if (!logo || !logo.url) return '';
  if (
    logo.url.startsWith('data:')
    || logo.url.startsWith('http:')
    || logo.url.startsWith('https:')
  ) {
    return logo.url;
  }
  const baseUrl = import.meta.env.BASE_URL || '/';
  if (baseUrl !== '/' && logo.url.startsWith(baseUrl)) {
    return logo.url;
  }
  return (baseUrl + logo.url).replace(/\/+/g, '/');
});

const resolvedDefaultLogoUrl = computed(() => {
  const logo = defaultLogo.value;
  if (!logo || !logo.url) return '';
  if (
    logo.url.startsWith('data:')
    || logo.url.startsWith('http:')
    || logo.url.startsWith('https:')
  ) {
    return logo.url;
  }
  const baseUrl = import.meta.env.BASE_URL || '/';
  if (baseUrl !== '/' && logo.url.startsWith(baseUrl)) {
    return logo.url;
  }
  return (baseUrl + logo.url).replace(/\/+/g, '/');
});

const fileInput = ref<HTMLInputElement | null>(null);

const containerStyle = computed(() => {
  if (!theme.value) return {};
  return {
    // paddingBottom: `${theme.value.spacing.sectionGap}px`,
    // borderBottom: `1px solid ${theme.value.colors.border}`,
  };
});

const headingStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.heading.family,
    fontSize: `${theme.value.fonts.heading.size}px`,
    fontWeight: theme.value.fonts.heading.weight,
    color: theme.value.colors.text,
    margin: 0,
  };
});

function triggerUpload() {
  fileInput.value?.click();
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    const base64 = e.target?.result as string;
    const img = new Image();
    img.onload = () => {
      reportStore.setValue('header.addedLogo', {
        url: base64,
        width: img.width,
        height: img.height,
      });
    };
    img.src = base64;
  };
  reader.readAsDataURL(file);
}

function removeAddedLogo() {
  reportStore.setValue('header.addedLogo', null);
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}
</script>

<template>
  <div class="header-section" :style="containerStyle">
    <div class="header-content">
      <h1 class="header-heading" :style="headingStyle">
        {{ section.heading }}
      </h1>
    </div>

    <div class="header-logos">
      <!-- Added Logo (Secondary) -->
      <div v-if="addedLogo" class="logo-wrapper">
        <img
          :src="resolvedAddedLogoUrl"
          :alt="addedLogo.alt || 'Added Logo'"
          class="header-logo-img"
          :style="addedLogoStyle"
        >
        <button
          v-if="isEditing"
          class="delete-btn"
          title="Remove logo"
          @click="removeAddedLogo"
        >
          ×
        </button>
      </div>
      <div
        v-else-if="isEditing"
        class="logo-upload-placeholder"
        :style="{ height: `${logoHeight}px`, width: `${logoHeight * 1.5}px` }"
        title="Upload additional logo"
        @click="triggerUpload"
      >
        <span>+ Logo</span>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onFileChange"
        >
      </div>

      <!-- Default Logo (Primary, rightmost) -->
      <div v-if="defaultLogo" class="logo-wrapper">
        <img
          :src="resolvedDefaultLogoUrl"
          :alt="defaultLogo.alt || 'Logo'"
          class="header-logo-img"
          :style="defaultLogoStyle"
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  flex: 1;
}

.header-heading {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.header-logos {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.header-logo-img {
  object-fit: contain;
}

.delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  line-height: 1;
  padding: 0;
}

.delete-btn:hover {
  background: #dc2626;
}

.logo-upload-placeholder {
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  user-select: none;
}

.logo-upload-placeholder:hover {
  border-color: #0284c7;
  color: #0284c7;
  background: #f0f9ff;
}
</style>
